import {
  defineBreakpoints,
  defineComponents,
  defineDesignTokens,
} from "@contentful/experiences-sdk-react";
import { borderRadius, spacing } from "./app/theme/tokens";
import { ad2Colors, textColors, backgrounds } from "./app/theme/palette";
import { STUDIO_QUERY } from "./lib/constants/mediaQueries";
import { STUDIO_COMPONENTS, STUDIO_BUILT_IN_COMPONENT_IDS } from "./studio-components";

defineComponents(STUDIO_COMPONENTS, {
  enabledBuiltInComponents: STUDIO_BUILT_IN_COMPONENT_IDS,
  experimentalComponents: {
    carousel: false,
  },
});

// fontSize / lineHeight / letterSpacing tokens below are the mobile-first
// (pre-media-query) base values lifted verbatim from app/theme/typography.ts.
// The SDK's DesignTokensDefinition only accepts flat Record<string,string>
// maps for these three categories (no responsive/breakpoint dimension) and
// explicitly marks the `text` category (which is where fontWeight lives) as
// "Do not use - in the process of implementing" per
// @contentful/experiences-core dist/types/index.d.ts:657-683. fontWeight is
// therefore intentionally NOT registered as a design token here.
// The /smile header blue, exposed to authors by name. Chris Mason asked for it
// on 2026-09-08 so Bailey Hill's team can use the same blue for text, icons
// and backgrounds without a developer: "smileBlue" is the header's #0046F8
// (the /smile campaign's Primary Blue, see ad2Colors) and "smileBlueTint" is
// the pale #E7F0FC the header's utility strip and the scheduler's overflow chip
// sit on. Text gets the solid blue only; the tint is a background. CTAs already
// use this blue through the Smile Button's Solid style.
const smileTextColors = { ...textColors, smileBlue: ad2Colors.primaryBlue };
const smileBackgrounds = {
  ...backgrounds,
  smileBlue: ad2Colors.primaryBlue,
  smileBlueTint: ad2Colors.primaryBlueTint,
};

defineDesignTokens({
  textColor: smileTextColors,
  spacing,
  borderRadius,
  color: smileBackgrounds,
  fontSize: {
    headingXXL: "3.5rem", // typography.ts:118
    headingXL: "2.75rem", // typography.ts:132
    headingL: "2.25rem", // typography.ts:146
    headingM: "1.75rem", // typography.ts:160
    headingS: "1.5rem", // typography.ts:173
    headingXS: "1.25rem", // typography.ts:186
    headingLAd2: "2.25rem", // typography.ts:204
    bodyXL: "1.25rem", // typography.ts:46
    bodyL: "1rem", // typography.ts:60
    bodyM: "0.875rem", // typography.ts:74
    bodyS: "0.875rem", // typography.ts:88
    bodyXS: "0.75rem", // typography.ts:98
  },
  lineHeight: {
    headingXXL: "4.25rem", // typography.ts:119
    headingXL: "3.5rem", // typography.ts:133
    headingL: "2.75rem", // typography.ts:147
    headingM: "2rem", // typography.ts:161
    headingS: "1.75rem", // typography.ts:174
    headingXS: "1.5rem", // typography.ts:187
    headingLAd2: "2.7rem", // typography.ts:205
    bodyXL: "1.75rem", // typography.ts:47
    bodyL: "1.5rem", // typography.ts:61
    bodyM: "1.25rem", // typography.ts:75
    bodyS: "1.25rem", // typography.ts:89
    bodyXS: "1rem", // typography.ts:99
    bodyRelaxed: "1.65", // typography.ts:266/272 (unitless, northstar relaxed ramp)
  },
  letterSpacing: {
    bodyCommon: "-0.5px", // typography.ts:40
  },
});

defineBreakpoints([
  {
    id: "mobile",
    query: "*",
    displayName: "All Sizes",
    displayIcon: "mobile",
    previewSize: "390px", // iPhone 12 Pro
  },
  {
    id: "tablet",
    query: STUDIO_QUERY.TABLET,
    displayName: "Tablet",
    displayIcon: "tablet",
    previewSize: "820px", // iPad Air
  },
  {
    id: "desktop",
    query: STUDIO_QUERY.DESKTOP,
    displayName: "Desktop",
    displayIcon: "desktop",
  },
  {
    id: "large-desktop",
    query: STUDIO_QUERY.LARGE_DESKTOP,
    displayName: "Large Desktop",
    displayIcon: "desktop",
  },
]);
